<template>
	<div
		class="nav notification"
		id="headerNotifications"
		:class="itemsVisible && 'is-open'"
	>
		<VueNotificationBell
			:counter="activeCount"
			:hasDisplayLimit="true"
			@bellClicked="toggleShowItemsWrapper"
			:isOpen="itemsVisible"
		/>
		<div
			class="notification__list"
			v-show="itemsVisible && !notificationModalVisible"
		>
			<div class="notification__loading" v-show="loadingItems">
				<i class="icon icon-loading">
					<span class="path1"></span><span class="path2"></span>
				</i>
			</div>
			<h3 class="notification__header">
				Manage Notifications
			</h3>
			<Tabs
				:tabs="tabs"
				:selectedTabIndex="selectedTab"
				contentMaxHeight="586px"
				variant="highlight1"
			>
				<template v-slot:headerItem1
					>Active ({{ activeCount }})</template
				>
				<template v-slot:headerItem2>
					Dismissed ({{ dismissedCount }})
				</template>
				<template v-slot:1>
					<VueNotificationEmptyListMessage
						labelText="You have no active notifications."
						v-if="activeNotificationItems.length === 0"
					/>
					<InfiniteScroller
						class="notification__infinity-scroll"
						@scrollBoundary="onScrollBoundaryActive"
						:loading="activeLoading"
						:hasMoreResults="hasMoreActiveResults"
						v-if="activeNotificationItems.length > 0"
					>
						<template v-slot:scrollDiv>
							<VueNotificationList
								:items="activeNotificationItems"
								@notificationClick="onNotificationClick"
								@dismissClick="onNotificationDismissed"
								@saveClick="onNotificationDismissed"
							/>
						</template>
					</InfiniteScroller>
				</template>
				<template v-slot:2>
					<VueNotificationEmptyListMessage
						labelText="You have no dismissed notifications."
						v-if="dismissedNotificationItems.length === 0"
					/>
					<InfiniteScroller
						class="notification__infinity-scroll"
						@scrollBoundary="onScrollBoundaryDismissed"
						:loading="dismissedLoading"
						:hasMoreResults="hasMoreDismissedResults"
						v-if="dismissedNotificationItems.length > 0"
					>
						<template v-slot:scrollDiv>
							<VueNotificationList
								:items="dismissedNotificationItems"
								@notificationClick="onNotificationClick"
							/>
						</template>
					</InfiniteScroller>
				</template>
			</Tabs>
		</div>
		<IframeContainer
			v-if="notificationModalVisible"
			:source="iframeSource"
		/>
	</div>
</template>

<script>
import VueNotificationBell from './vue-notification-bell.vue';
import VueNotificationList from './vue-notification-list.vue';
import getCompanyId from '@/composables/get-companyid';
import { mapGetters, mapActions } from 'vuex';
import IframeContainer from '@/components/shells/iframe-container/iframe-container.vue';
import Tabs from '@/components/shells/tabs/tabs.vue';
import InfiniteScroller from '@/components/shells/infinite-scroller/infinite-scroller.vue';
import VueNotificationEmptyListMessage from './vue-notifications-empty-list-message.vue';

export default {
	name: 'VueNotifications',
	components: {
		VueNotificationBell,
		IframeContainer,
		VueNotificationList,
		Tabs,
		InfiniteScroller,
		VueNotificationEmptyListMessage
	},
	data() {
		return {
			itemsVisible: false,
			loadingItems: false,
			response: { totalCount: 0 },
			modalWidth: 'auto',
			modalHeight: 'auto',
			selectedTab: 1,
			activePage: 1,
			dismissedPage: 1,
			activeLoading: false,
			dismissedLoading: false,
			scrollElement: undefined,
			tabs: [
				{
					index: 1,
					title: 'Active'
				},
				{
					index: 2,
					title: 'Dismissed'
				}
			]
		};
	},
	props: {
		activePageSize: {
			type: Number,
			default: 10
		},
		dismissedPageSize: {
			type: Number,
			default: 10
		}
	},
	async created() {
		document.addEventListener(
			'mouseup',
			this.globalMouseUpHandler.bind(this)
		);

		await Promise.all([
			this.getActiveNotifications(this.activePage),
			this.getDismissedNotifications(this.dismissedPage)
		]);

		window.addEventListener('message', this.onMessage);
	},
	beforeDestroy() {
		window.removeEventListener('message', this.onMessage);
	},
	computed: {
		...mapGetters('user', [
			'notificationModalVisible',
			'activeNotificationItems',
			'dismissedNotificationItems',
			'activeNotificationCount',
			'dismissedNotificationCount',
			'activeNotificationPageCount',
			'dismissedNotificationPageCount',
			'iframeSource'
		]),
		notificationManagerLink() {
			return `/System/NotificationManager?cid=${getCompanyId(
				this.$route
			)}`;
		},
		activeCount() {
			return this.activeNotificationCount;
		},
		dismissedCount() {
			return this.dismissedNotificationCount;
		},
		hasMoreActiveResults() {
			let result = false;

			result = this.activeNotificationItems.length < this.activeCount;

			return result;
		},
		hasMoreDismissedResults() {
			let result = false;

			result =
				this.dismissedNotificationItems.length < this.dismissedCount;

			return result;
		}
	},
	methods: {
		...mapActions('user', [
			'hideNotificationModalAction',
			'getActiveNotificationsAction',
			'getDismissedNotificationsAction',
			'dismissNotificationAction'
		]),
		async getActiveNotifications(page) {
			page = page || 1;
			this.activeLoading = true;

			await this.getActiveNotificationsAction({
				companyId: getCompanyId(this.$route),
				page: page,
				pageSize: this.activePageSize
			});

			this.activeLoading = false;
		},
		async getDismissedNotifications(page) {
			page = page || 1;
			this.dismissedLoading = true;

			await this.getDismissedNotificationsAction({
				companyId: getCompanyId(this.$route),
				page: page,
				pageSize: this.dismissedPageSize
			});

			this.dismissedLoading = false;
		},
		/**
		 * Toggles visibility
		 */
		toggleShowItemsWrapper() {
			this.itemsVisible = !this.itemsVisible;
		},
		/**
		 * Make sure clicking outside the notification area
		 * will hide notifications dropdown
		 */
		globalMouseUpHandler(e) {
			var container = document.getElementById('headerNotifications');
			if (e && !container.contains(e.target) && this.itemsVisible) {
				this.toggleShowItemsWrapper();
			}
		},
		onScrollBoundaryActive() {
			if (this.activePage + 1 <= this.activeNotificationPageCount) {
				this.activePage += 1;
				this.getActiveNotifications(this.activePage);
			}
		},
		onScrollBoundaryDismissed() {
			if (this.dismissedPage + 1 <= this.dismissedNotificationPageCount) {
				this.dismissedPage += 1;
				this.getDismissedNotifications(this.dismissedPage);
			}
		},
		onCloseModal() {
			this.hideNotificationModalAction();
		},
		onMessage(e) {
			if (
				e &&
				e.data.source === 'AccessToVue' &&
				e.data.data === 'vue.closeNotificationModal'
			) {
				console.log('vue-ui onMessage');
				console.log(e);
				this.onCloseModal();
			}
		},
		onNotificationClick() {
			this.itemsVisible = false;
		},
		async onNotificationDismissed(params) {
			await this.dismissNotificationAction(params);

			if (
				this.activeNotificationPageCount > 1 &&
				this.activePage + 1 <= this.activeNotificationPageCount
			) {
				// Call api to re-get pages that have already been loaded and the next page.
				// Calling in reverse page order to prevent duplicate keys in the list.
				for (let i = this.activePage + 1; i > 0; i--) {
					await this.getActiveNotifications(i);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Notification
    * Can use SCSS variables = TRUE
*/
.notification {
	position: relative;
	@include header-menu-item('.notification-bell', 'vue-notification');

	&__header {
		margin-bottom: $gutter-md;
		font-size: $heading-font-size-md;
		font-weight: $bold-font-weight;
	}

	&__loading {
		display: flex;
		justify-content: center;
		font-size: 30px;

		.icon-loading {
			@include animation-spin('.8s');
		}
	}

	&__empty-list {
		margin: $gutter-xl 0;
	}

	&__empty-list-img {
		max-width: 250px;
	}

	&__empty-list-text {
		text-align: center;
		margin-top: $gutter-lg;
		font-size: $heading-font-size-md;
		font-weight: $regular-font-weight;
		line-height: $heading-line-height-sm;
	}

	&__list {
		position: fixed;
		width: 100%;
		height: calc(100% - 49px);
		top: 49px;
		left: 0;
		z-index: $z-level1-2;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		padding: $padding-md;
		cursor: initial;

		@include mq($from: sm-l) {
			position: absolute;
			width: 320px;
			height: auto;
			max-height: 700px;
			top: 100%;
			left: 50%;
			transform: translateX(-55%);
			border: 1px solid var(--menu-list-border);
			border-radius: $submenu-border-radius;
			@include medium-shadow;
		}
	}

	&__infinity-scroll::v-deep {
		.infinite-scroller__container-elements {
			margin: $gutter-lg 0 0;
		}

		.infinite-scroller__loading {
			display: flex;
		}
	}

	&__pagination {
		display: flex;
		justify-content: space-between;
		margin-top: $gutter-lg;

		> * {
			@include clear-btn-styles;
			color: var(--link);
			border-bottom: 1px solid transparent;

			&:hover {
				color: var(--link-hover);
				border-color: var(--link-hover);
			}
		}

		&-next {
			margin-left: auto;
		}
	}
}
</style>
